import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema, moneySchema } from './common';
import { expenseSchema } from './expenses';
import { memberBalanceSchema } from './groups';
import { settlementSchema } from './settlements';
import { userPublicSchema } from './users';

export const memberNetSchema = z.object({
  memberId: idSchema,
  /** Positive = is owed, negative = owes (minor units of the enclosing currency). */
  amount: z.int(),
});
export type MemberNet = z.infer<typeof memberNetSchema>;

export const transferSchema = z.object({
  fromMemberId: idSchema,
  toMemberId: idSchema,
  amount: moneySchema,
});
export type TransferDto = z.infer<typeof transferSchema>;

/**
 * `GET /api/groups/:id/balances`. `debts` are the simplified debts when the group has
 * `simplifyDebts` on, otherwise the raw pairwise "who owes whom".
 */
export const groupBalancesSchema = z.object({
  groupId: idSchema,
  currency: z.string(),
  simplifyDebts: z.boolean(),
  /** Everything converted to the group currency with each item's stored rate. */
  converted: z.object({
    balances: z.array(memberNetSchema),
    debts: z.array(transferSchema),
  }),
  /** One entry per currency, never converted ("Not converted"). */
  byCurrency: z.array(
    z.object({
      currency: z.string(),
      balances: z.array(memberNetSchema),
      debts: z.array(transferSchema),
    }),
  ),
  /** Total spent per currency (non-deleted expenses). */
  totalSpent: z.array(moneySchema),
  /** Date of the ECB table used for items whose stored rate targets another currency. */
  fallbackRatesDate: isoDateSchema.nullable(),
});
export type GroupBalances = z.infer<typeof groupBalancesSchema>;

/** One person's balance with the caller (positive = they owe you). */
export const personBalanceSchema = z.object({
  user: userPublicSchema,
  /** Converted to the caller's default currency at today's ECB rates (show with ≈). */
  net: moneySchema,
  byCurrency: z.array(moneySchema),
});
export type PersonBalance = z.infer<typeof personBalanceSchema>;

/** One real per-currency component of the overview, with its converted value. */
export const overviewComponentSchema = moneySchema.extend({
  /** `amount` in the caller's default currency at today's ECB rates (≈); `null` if no rate. */
  converted: moneySchema.nullable(),
});
export type OverviewComponent = z.infer<typeof overviewComponentSchema>;

/** `GET /api/balances`: Home overview. */
export const overviewSchema = z.object({
  /** The caller's default currency. */
  currency: z.string(),
  ratesDate: isoDateSchema.nullable(),
  /** Net across everything in `currency` at today's rates (≈). Positive = you are owed. */
  net: moneySchema,
  /** Sum of positive group nets, converted (≈). */
  owed: moneySchema,
  /** Sum of negative group nets as a positive amount, converted (≈). */
  owing: moneySchema,
  /**
   * Real per-currency components, never merged: "You owe €36.23 + $18.00". Each one also
   * carries its converted value (positive, like `amount`) so clients can order them.
   */
  components: z.object({
    owed: z.array(overviewComponentSchema),
    owing: z.array(overviewComponentSchema),
  }),
  groups: z.array(
    z.object({
      groupId: idSchema,
      balance: memberBalanceSchema,
      lastActivityAt: isoDateTimeSchema,
    }),
  ),
  /** Non-zero people only, sorted by absolute converted amount. */
  people: z.array(personBalanceSchema),
  /** Pending settlements the caller has to confirm. */
  attentionCount: z.int(),
});
export type Overview = z.infer<typeof overviewSchema>;

export const peopleListSchema = z.object({ items: z.array(personBalanceSchema) });
export type PeopleList = z.infer<typeof peopleListSchema>;

/** `GET /api/people/:userId`: 1:1 balance across shared groups. */
export const personDetailSchema = personBalanceSchema.extend({
  groups: z.array(
    z.object({
      groupId: idSchema,
      kind: z.enum(['group', 'direct']),
      name: z.string(),
      emoji: z.string().nullable(),
      /** In the group currency; positive = they owe you. */
      amount: moneySchema,
      byCurrency: z.array(moneySchema),
    }),
  ),
  /** The hidden 1:1 group, if it exists (create it with `POST /api/direct`). */
  directGroupId: idSchema.nullable(),
});
export type PersonDetail = z.infer<typeof personDetailSchema>;

/** Group feed item (`GET /api/groups/:id/feed`): expenses and settlements interleaved. */
export const feedItemSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('expense'), expense: expenseSchema }),
  z.object({ type: z.literal('settlement'), settlement: settlementSchema }),
]);
export type FeedItem = z.infer<typeof feedItemSchema>;
